import { useState } from 'react';
import { Mail, Phone, MapPin, MessageCircle, Clock, Send, CheckCircle, ArrowRight, ExternalLink } from 'lucide-react';
import { FacebookIcon, InstagramIcon } from '../components/ui/SocialIcons';
import { usePageSEO } from '../hooks/usePageSEO';

const contactInfo = [
  {
    icon: Phone,
    title: 'Call Directly',
    value: '0305-6367673',
    href: 'tel:03056367673',
    desc: 'Mon – Sat, 10:00 AM – 8:00 PM',
    badge: 'Fast Response',
    badgeColor: 'bg-emerald-50 text-emerald-700',
  },
  {
    icon: MessageCircle,
    title: 'WhatsApp Support',
    value: '0305-6367673',
    href: 'https://wa.me/923056367673',
    desc: 'Instant consultation & ordering',
    badge: 'Available 24/7',
    badgeColor: 'bg-green-50 text-green-700',
    external: true,
  },
  {
    icon: Mail,
    title: 'Email Us',
    value: 'wellzoraherbalcare@gmail.com',
    href: 'mailto:wellzoraherbalcare@gmail.com',
    desc: 'We reply within 24 hours',
    badge: 'Inquiries',
    badgeColor: 'bg-blue-50 text-blue-700',
  },
];

const socialPlatforms = [
  {
    name: 'WhatsApp Consultation',
    username: '0305-6367673',
    desc: 'Chat with our herbal specialists',
    url: 'https://wa.me/923056367673',
    actionText: 'Message Now',
    iconBg: 'bg-emerald-500 text-white shadow-emerald-500/30',
    hoverBorder: 'hover:border-emerald-500/40 hover:bg-emerald-50/30',
    badge: 'Instant Reply',
    icon: MessageCircle,
  },
  {
    name: 'Facebook Page',
    username: '@wellzoraherbalcare',
    desc: 'Official updates, reviews & tips',
    url: 'https://www.facebook.com/share/198qJNqxMp/?mibextid=wwXIfr',
    actionText: 'Follow Us',
    iconBg: 'bg-[#1877F2] text-white shadow-blue-500/30',
    hoverBorder: 'hover:border-blue-500/40 hover:bg-blue-50/30',
    badge: 'Official',
    icon: FacebookIcon,
  },
  {
    name: 'Instagram Profile',
    username: '@wellzora_herbal_care',
    desc: 'Product showcases & wellness advice',
    url: 'https://www.instagram.com/wellzora_herbal_care?igsi=YmQxanF4dzZmOWd6&utm_source=qr',
    actionText: 'Visit Profile',
    iconBg: 'bg-gradient-to-tr from-amber-500 via-rose-500 to-purple-600 text-white shadow-pink-500/30',
    hoverBorder: 'hover:border-pink-500/40 hover:bg-pink-50/30',
    badge: 'Trending',
    icon: InstagramIcon,
  },
];

export function Contact() {
  usePageSEO({
    title: 'Contact WellZora Herbal Care | Free WhatsApp Herbal Consultation',
    description: 'Get in touch with WellZora Herbal Care specialists in Lahore, Pakistan. Call 0305-6367673 or message on WhatsApp for customized herbal health consultations.',
    canonical: 'https://wellzoraherbalcare.com/contact',
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.phone.trim() || !formData.message.trim()) {
      setStatus('error');
      setSubmitMessage('Please fill in your name, phone number, and message.');
      return;
    }
    
    setStatus('success');
    setSubmitMessage('Thank you! Your message has been sent. We will respond promptly.');
    
    // Also provide option to open WhatsApp directly with their inquiry
    const waText = encodeURIComponent(
      `Hello WellZora! My name is ${formData.name}. Subject: ${formData.subject || 'General Inquiry'}\nMessage: ${formData.message}`
    );
    setTimeout(() => {
      window.open(`https://wa.me/923056367673?text=${waText}`, '_blank');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setStatus('idle');
    }, 1200);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Header */}
      <section className="bg-gradient-to-br from-primary-dark via-primary to-herbal-700 relative overflow-hidden text-white py-16">
        <div className="absolute inset-0 hero-pattern opacity-30 pointer-events-none" />
        <div className="absolute -bottom-10 right-10 w-80 h-80 bg-accent/15 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center sm:text-left">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white/15 backdrop-blur-md rounded-full text-xs font-semibold tracking-wide uppercase mb-4 text-white/90">
            💬 Get In Touch
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            We're Here to Help You Heal Naturally
          </h1>
          <p className="text-white/80 text-base sm:text-lg max-w-2xl leading-relaxed">
            Have questions about our herbal formulations or need customized health guidance? Connect with our experienced herbal consultants today.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Quick Contact Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {contactInfo.map((info, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between group"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-primary/10 group-hover:bg-primary/20 rounded-xl flex items-center justify-center transition-colors">
                      <info.icon className="w-6 h-6 text-primary" />
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${info.badgeColor}`}>
                      {info.badge}
                    </span>
                  </div>
                  <h3 className="font-bold text-gray-900 text-base mb-1">{info.title}</h3>
                  <p className="text-gray-500 text-xs mb-3">{info.desc}</p>
                </div>
                
                {info.href ? (
                  <a 
                    href={info.href} 
                    target={info.external ? '_blank' : undefined}
                    rel={info.external ? 'noopener noreferrer' : undefined}
                    className="inline-flex items-center gap-2 text-primary font-bold text-sm hover:text-primary-dark group-hover:translate-x-1 transition-all pt-2 border-t border-gray-100"
                  >
                    <span>{info.value}</span>
                    <ArrowRight className="w-4 h-4" />
                  </a>
                ) : (
                  <p className="text-gray-900 font-semibold text-sm pt-2 border-t border-gray-100">{info.value}</p>
                )}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Left: Message Form */}
            <div className="lg:col-span-7 bg-white rounded-3xl p-6 sm:p-10 border border-gray-100 shadow-md">
              <div className="mb-8">
                <span className="text-xs font-bold text-primary tracking-wider uppercase">Direct Message</span>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">Send Us a Message</h2>
                <p className="text-gray-500 text-sm mt-2">
                  Fill in your details below and our team will get in touch with you immediately.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="input-field"
                      required
                      placeholder="e.g. Wajeeh Ur Rehman"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                      Phone / WhatsApp <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input-field"
                      required
                      placeholder="03XX-XXXXXXX"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="email" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                      Email Address <span className="text-gray-400 font-normal">(Optional)</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                      Subject
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="input-field"
                    >
                      <option value="">Select Topic</option>
                      <option value="Product Recommendation">Product Recommendation</option>
                      <option value="Order Status & Delivery">Order Status & Delivery</option>
                      <option value="Health Consultation">Herbal Health Consultation</option>
                      <option value="Bulk / Wholesale Inquiry">Bulk / Wholesale Inquiry</option>
                      <option value="Other">Other Inquiry</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                    Your Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="input-field min-h-[110px] resize-y"
                    required
                    placeholder="Describe your health goal, symptoms, or product questions..."
                  />
                </div>

                {status === 'error' && (
                  <div className="p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-2">
                    <span>⚠️</span>
                    <span>{submitMessage}</span>
                  </div>
                )}

                {status === 'success' && (
                  <div className="p-3.5 rounded-xl bg-green-50 border border-green-200 text-green-700 text-xs flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span>{submitMessage}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'success'}
                  className="w-full inline-flex items-center justify-center gap-2.5 py-4 px-6 rounded-2xl bg-gradient-to-r from-primary via-primary-dark to-herbal-800 text-white font-bold text-base shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-50 cursor-pointer"
                >
                  <Send className="w-4 h-4" />
                  <span>Send Message & Connect on WhatsApp</span>
                </button>
              </form>
            </div>

            {/* Right: Social Platforms & Business Info */}
            <div className="lg:col-span-5 space-y-6">
              {/* Connect Card */}
              <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-100 shadow-md">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Connect With Us</h3>
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
                </div>
                <p className="text-gray-500 text-xs mb-6">
                  Click below to visit our official channels for direct chat, reels, and exclusive herbal tips.
                </p>

                <div className="space-y-3.5">
                  {socialPlatforms.map((platform, idx) => (
                    <a
                      key={idx}
                      href={platform.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center justify-between p-4 rounded-2xl border border-gray-100 bg-gray-50/70 transition-all duration-300 group shadow-sm hover:shadow-md ${platform.hoverBorder}`}
                    >
                      <div className="flex items-center gap-3.5 min-w-0">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shadow-md flex-shrink-0 ${platform.iconBg}`}>
                          <platform.icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-bold text-gray-900 text-sm truncate">{platform.name}</h4>
                          </div>
                          <p className="text-xs text-gray-500 truncate">{platform.username}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 text-xs font-semibold text-primary group-hover:translate-x-1 transition-transform flex-shrink-0 pl-2">
                        <span>{platform.actionText}</span>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Operating Hours Card */}
              <div className="bg-gradient-to-br from-gray-900 to-gray-950 text-white rounded-3xl p-6 sm:p-8 shadow-md">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-primary-light" />
                  </div>
                  <div>
                    <h4 className="font-bold text-base text-white">Consultation Hours</h4>
                    <p className="text-xs text-gray-400">Pakistan Standard Time (PKT)</p>
                  </div>
                </div>

                <div className="space-y-3 text-xs border-y border-gray-800 py-4 mb-4">
                  <div className="flex justify-between items-center text-gray-300">
                    <span>Monday – Saturday:</span>
                    <span className="font-semibold text-white">10:00 AM – 8:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center text-gray-300">
                    <span>Sunday:</span>
                    <span className="font-semibold text-white">10:00 AM – 6:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center text-gray-300">
                    <span>WhatsApp Orders:</span>
                    <span className="font-semibold text-emerald-400 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
                      24/7 Accepted
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <MapPin className="w-4 h-4 text-accent flex-shrink-0" />
                  <span>Head Office: Lahore, Pakistan</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}